import React, { useState, useEffect, useContext } from "react";

import {
  Badge,
  Box,
  Button,
  Card,
  Cell,
  DatePicker,
  IconButton,
  Image,
  Layout,
  Loader,
  MessageModalLayout,
  Modal,
  Page,
  Pagination,
  Search,
  Table,
  TableListItem,
  TableToolbar,
  Tabs,
  Text,
  TextButton,
  Tooltip,
  WixDesignSystemProvider,
  accordionItemBuilder,
} from "@wix/design-system";
import "@wix/design-system/styles.global.css";
import * as Icons from "@wix/wix-ui-icons-common";

import searchLogo from "./../search.svg";
import { withProviders } from "../../withProviders";
import WixLogo from "../wix_logo.svg";
import FiareLogo from "../fiare_logo.svg";
import "../index.css";
import { useDebouncedCallback } from "use-debounce";
import Orders from "../../../models/orders";
import { truncateText } from "../../../services/utils/productUtils";
import { showToast } from "@wix/dashboard-sdk";
import Loading from "../../../components/common/Loader";
import { useFaireOrdersCount } from "../../../hooks/faire/useFaire";
import SidePanelModal from "../../../components/common/modal/SidePanelModal";
import CardModal from "../../../components/common/modal/CardModal";
import SiteContext from "../../../contexts/site-context";
import Pusher from "pusher-js";
import Site from "../../../models/site";
import TrialExpiryAlert from "../../../components/trial-expiry-alert";
import { pusherConfig } from "../../../config/pusher";
import { usePusherImportProgress } from "../../../hooks/usePusher";
import "../index.css";
import SyncedDate from "../../../components/common/SyncedDate";

type FilterParameter = {
  keywords: string;
  status: string | number;
  order_date?: Date | string;
  page?: number;
  perPage?: number;
};

const Index = () => {
  /*
  |--------------------------------------------------------------------------
  | Pusher Implementation 
  |--------------------------------------------------------------------------
  |
  */
  const { siteId } = useContext(SiteContext);

  // Review Modal
  const [modalShown, setModalShow] = useState(false);
  const [siteDetails, setSiteDetails] = useState<any>({});

  let pusher = new Pusher(pusherConfig.app_key, {
    cluster: pusherConfig.app_cluster,
  });

  useEffect(() => {
    if (siteId !== "") {
      // Review Modal Channel
      let channel = pusher.subscribe(siteId + "-review-collection");
      channel.bind("reviewCollection", function (data: any) {
        setModalShow(true);
        console.log("pusher running");
      });
    }
  }, [siteId]);

  // Fetch Order Progress
  const { setImportInProgress, importInProgress, setProgress, progress } = usePusherImportProgress({
    siteId: siteId,
    broadcastChannel: `${siteId}-fetch-faire-orders`,
    broadcastAs: "fetchFaireOrders.progressed",
  });

  React.useEffect(() => {
    if (progress === 100) {
      setImportInProgress(false);
      setProgress(0);
      fetchData();
      refetch(); // Refetch Orders Count
    }
  }, [progress]);

  /*
  |--------------------------------------------------------------------------
  | States
  |--------------------------------------------------------------------------
  |
  */

  const [orders, setOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  /*
    |--------------------------------------------------------------------------
    | Filter parameters
    |-------------------------------------------------------------------  -------
    |
    */

  const [keywords, setKeywords] = useState("");
  const [status, setStatus] = useState("");
  const [date, setDate] = useState("");

  /*
    |--------------------------------------------------------------------------
    | Get filter parameters
    |--------------------------------------------------------------------------
    |
    */
  const getFilterParameters = (): FilterParameter => {
    return {
      keywords: keywords,
      status: status,
      order_date: date,
    };
  };

  /*
    |--------------------------------------------------------------------------
    | Pagination details
    |--------------------------------------------------------------------------
    |
    */
  const [totalPage, setTotalPage] = useState(1);
  const [currentPageNumber, setCurrentPageNumber] = useState(1);
  const itemsPerPage = 10;

  /*
  |--------------------------------------------------------------------------
  | Manage Order Counts 
  |--------------------------------------------------------------------------
  |
  */
  const { data: orderCounts, isSuccess, refetch } = useFaireOrdersCount();
  const countData = isSuccess ? orderCounts?.data : null;

  /*
  |--------------------------------------------------------------------------
  | Count Display Conditions
  |--------------------------------------------------------------------------
  |
  */
  const allOrderCountCondition = countData?.all_order && countData?.all_order !== 0;
  const fulfilledOrderCountCondition = countData?.fulfilled_orders && countData?.fulfilled_orders !== 0;
  const unfulfilledOrderCountCondition = countData?.unfulfilled_orders && countData?.unfulfilled_orders !== 0;
  const cancelledOrderCountCondition = countData?.cancelled_orders && countData?.cancelled_orders !== 0;

  /*
  |--------------------------------------------------------------------------
  | Status Option
  |--------------------------------------------------------------------------
  |
  */
  const statusOptions = [
    {
      id: "",
      title: (
        <Tooltip maxWidth={360} content="Here is the list of all the orders in your Faire store.">
          <Text>
            All Orders
            {allOrderCountCondition ? ` (${countData?.all_order})` : ""}
          </Text>
        </Tooltip>
      ),
    },

    {
      id: "Fulfilled",
      title: (
        <Tooltip maxWidth={360} content="Here is the list of orders that have been fulfilled by Faire.">
          <Text>
            Fulfilled
            {fulfilledOrderCountCondition ? ` (${countData?.fulfilled_orders})` : ""}
          </Text>
        </Tooltip>
      ),
    },
    {
      id: "Unfullfilled",
      title: (
        <Tooltip maxWidth={360} content="Here is the list of orders that have not been fulfilled by Faire, yet.">
          <Text>
            Unfulfilled
            {unfulfilledOrderCountCondition ? ` (${countData?.unfulfilled_orders})` : ""}
          </Text>
        </Tooltip>
      ),
    },
    {
      id: "Cancelled",
      title: (
        <Tooltip maxWidth={360} content="Here is the list of your cancelled orders in Faire.">
          <Text>
            Cancelled
            {cancelledOrderCountCondition ? ` (${countData?.cancelled_orders})` : ""}
          </Text>
        </Tooltip>
      ),
    },
  ];

  /*
  |--------------------------------------------------------------------------
  | Manage Side Panel
  |--------------------------------------------------------------------------
  |
  */
  const [showSidePanel, setShowSidePanel] = useState(false);

  const handleTabChange = (value: any) => {
    setStatus(value.id);
    setCurrentPageNumber(1);
  };

  const openPanel = (event: any) => setShowSidePanel(true); // Open Side Panel

  const closePanel = () => setShowSidePanel(false); // Close Side Panel

  /*
    |--------------------------------------------------------------------------
    | Date Change Handler
    |--------------------------------------------------------------------------
    |
    */

  const handleDateChange = (event: string) => {
    let dateArray = event.toString().split(" ");
    let year = dateArray[3];
    let monthName = dateArray[1];
    let day = dateArray[2];
    let month = 1;

    switch (monthName) {
      case "Feb":
        month = 2;
        break;
      case "Mar":
        month = 3;
        break;
      case "Apr":
        month = 4;
        break;
      case "May":
        month = 5;
        break;
      case "Jun":
        month = 6;
        break;
      case "Jul":
        month = 7;
        break;
      case "Aug":
        month = 8;
        break;
      case "Sep":
        month = 9;
        break;
      case "Oct":
        month = 10;
        break;
      case "Nov":
        month = 11;
        break;
      case "Dec":
        month = 12;
        break;
      default:
        month = 1;
    }

    let date = `${year}-${month}-${day}`;

    setDate(date);
    setCurrentPageNumber(1);
  };

  /*
    |--------------------------------------------------------------------------
    |  Handle page change
    |--------------------------------------------------------------------------
    |
    */

  const pageChangeHandler = (event: any) => {
    setCurrentPageNumber(event.page);
  };

  /*
    |--------------------------------------------------------------------------
    | Fetch Orders Data
    |--------------------------------------------------------------------------
    |
    */

  const fetchData = () => {
    let filterParameters = getFilterParameters();

    /*
        |--------------------------------------------------------------------------
        |  Append pagination query params
        |--------------------------------------------------------------------------
        |
        */
    filterParameters.page = currentPageNumber;
    filterParameters.perPage = itemsPerPage;

    // Set Loading
    setIsLoading(true);
    Orders.get(filterParameters)
      .then((response) => {
        let newOrders = [] as any;

        response.data.data.map((orders: any) => {
          newOrders.push({ ...orders, expandable: true, expanded: false });
        });

        setOrders(newOrders);

        const total = response.data.last_page;
        setTotalPage(total);
        setIsLoading(false);
      })
      .catch((error) => {
        console.error(error);
        setIsLoading(false);
      });
  };

  /*
    |--------------------------------------------------------------------------
    | Flag: whether importing faire orders is being fetched or not
    | Flag: should show confirmation about whether to fetch orders or not. 
    |--------------------------------------------------------------------------
    |
    */

  const [isFetchingOrders, setIsFetchingOrders] = useState(false);
  const [showConfirmationForImport, setShowConfirmationForImport] = useState(false);

  /*
    |--------------------------------------------------------------------------
    | Handle importing faire Orders
    |--------------------------------------------------------------------------
    |
    */
  const handleImportFaireOrders = () => {
    if (showConfirmationForImport) return;

    setShowConfirmationForImport(true);
  };

  /*
    |--------------------------------------------------------------------------
    | Handle confirmation
    |--------------------------------------------------------------------------
    |
    */

  const handleConfirmationForImport = (confirmationStatus: any) => {
    setShowConfirmationForImport(false);

    if (confirmationStatus) {
      fetchOrders();
    }
  };

  /*
    |--------------------------------------------------------------------------
    | Fetch faire Orders
    |--------------------------------------------------------------------------
    |
    */
  const fetchOrders = () => {
    if (isFetchingOrders) return;

    setIsFetchingOrders(true);

    Orders.import()
      .then((response) => {
        showToast({
          type: "success",
          message: "Your Faire Orders are being fetched.",
        });
      })
      .catch((error) => {
        console.error(error);
        showToast({
          type: "error",
          message: "Failed to fetch Orders. Please try again or contact support.",
        });
      })
      .finally(() => setIsFetchingOrders(false));
  };

  /*
    |--------------------------------------------------------------------------
    | Unfulfilled status
    |--------------------------------------------------------------------------
    |
    */

  // const unfulfilled = ["NEW", "PRE_TRANSIT", "PROCESSING", "BACKORDERED", "IN_TRANSIT"];

  /*
    |--------------------------------------------------------------------------
    | Render Badge
    |--------------------------------------------------------------------------
    |
    */
  const renderBadge = (row: any) => {
    return (
      <Badge
        size="tiny"
        skin={row?.state === "DELIVERED" ? "neutralSuccess" : row?.state === "CANCELED" ? "neutralDanger" : "neutral"}
      >
        {row?.state === "CANCELED" ? "CANCELLED" : row?.state === "DELIVERED" ? "FULFILLED" : row?.state}
      </Badge>
    );
  };

  /*
    |--------------------------------------------------------------------------
    | Flag: is sending to Wix is in progress?
    |--------------------------------------------------------------------------
    |
    */

  const [isSendingToWix, setIsSendingToWix] = React.useState(false);

  /*
    |--------------------------------------------------------------------------
    | Flag: whether to show 'Send to Wix' confirmation
    |--------------------------------------------------------------------------
    |
    */

  const [shouldShowSendToWixConfirmation, setShouldShowSendToWixConfirmation] = React.useState(false);

  /*
    |--------------------------------------------------------------------------
    | Selected order's ID to be sent to wix
    |--------------------------------------------------------------------------
    |
    */

  const [selectedOrderIdToBeSentToWix, setSelectedOrderIdToBeSentToWix] = React.useState("");

  /*
    |--------------------------------------------------------------------------
    | Handle send to Wix
    |--------------------------------------------------------------------------
    |
    */

  const handleSendToWix = (orderId: string) => {
    setSelectedOrderIdToBeSentToWix(orderId);
    refetch();
  };

  /*
    |--------------------------------------------------------------------------
    | Handle cancel 'Send to Wix' operation
    |--------------------------------------------------------------------------
    |
    */

  const handleCancelSendToWix = () => {
    setShouldShowSendToWixConfirmation(false);
    setSelectedOrderIdToBeSentToWix("");
  };

  /*
    |--------------------------------------------------------------------------
    | Handle 'Send to Wix' confirmation
    |--------------------------------------------------------------------------
    |
    */

  const handleSendToWixConfirmation = () => {
    setShouldShowSendToWixConfirmation(false);
    sendToWix();
  };

  /*
    |--------------------------------------------------------------------------
    | Send to wix
    |--------------------------------------------------------------------------
    |
    */

  const sendToWix = () => {
    if (isSendingToWix) return;
    setIsSendingToWix(true);

    Orders.sendToWix(selectedOrderIdToBeSentToWix)
      .then((response: any) => {
        showToast({
          type: "success",
          message: response.message,
        });

        fetchData();
      })
      .catch((error) => {
        showToast({
          type: "error",
          message: error.message,
        });
      })
      .finally(() => {
        setIsSendingToWix(false);
        setSelectedOrderIdToBeSentToWix("");
      });
  };

  React.useEffect(() => {
    if (selectedOrderIdToBeSentToWix !== "") {
      setShouldShowSendToWixConfirmation(true);
    }
  }, [selectedOrderIdToBeSentToWix]);

  /*
    |--------------------------------------------------------------------------
    | Table Columns
    |--------------------------------------------------------------------------
    |
    */

  const columns = [
    {
      title: "Order #",
      width: "10%",
      render: (row: any) => (
        <>
          <Text size="small" weight="bold">
            #{row.display_id}
          </Text>
        </>
      ),
    },

    {
      title: "Order Date",
      width: "10%",
      render: (row: any) => (
        <>
          <Text size="small">{row.ship_after}</Text>
        </>
      ),
    },
    {
      title: "Payment",
      width: "10%",
      render: (row: any) => (
        <>
          <div
            style={{
              display: "flex",

              gap: "3px",
              alignItems: "center",
            }}
          >
            {row.payment_initiated_at && row.state === "DELIVERED" ? (
              <Text skin="success" size="small">
                Paid
              </Text>
            ) : (
              <Text skin="error" size="small">
                Unpaid
              </Text>
            )}
          </div>
        </>
      ),
    },
    {
      title: "Order Status",
      width: "10%",
      render: (row: any) => <>{renderBadge(row)}</>,
    },
    {
      title: "Total Payout",
      width: "10%",
      render: (row: any) => (
        <>
          {row.currency} &nbsp;
          {row.payout_costs.total_payout.amount_minor > 0
            ? `${(row.payout_costs.total_payout.amount_minor / 100).toFixed(2)}`
            : "0.00"}
        </>
      ),
    },
    {
      title: "Products",
      width: "10%",
      render: (row: any) => <>{row.order_items.length} products</>,
    },
    {
      title: "Order Sync",
      width: "10%",
      render: (row: any) => (
        <>
          <Box verticalAlign="middle" gap="SP1">
            <Badge size="tiny" skin={row.sync_status === 0 ? "neutralDanger" : "neutralSuccess"}>
              {row.sync_status === 0 ? "NOT SYNCED" : "SYNCED"}
            </Badge>
            <Tooltip content={row.sync_status ? "Sent to Wix Store" : "Unable to send to Wix Store."}>
              <Icons.InfoCircleFilled />
            </Tooltip>
          </Box>
        </>
      ),
    },
    {
      title: "View on",
      width: "10%",
      render: (row: any) => (
        <Box placeItems="center" gap="10px">
          <Tooltip placement="top" appendTo="window" content="View the order on Faire dashboard">
            <a href={`https://www.faire.com/brand-portal/orders/${row.order_id}/order-fulfilment`} target="_blank">
              <img src={FiareLogo} />
            </a>
          </Tooltip>
          {row.wix_order_id && (
            <Tooltip appendTo="window" content="View the order on Wix dashboard">
              <a href={row.wix_details_url} target="_blank">
                <img width="32px" height="12px" src={WixLogo} />
              </a>
            </Tooltip>
          )}
        </Box>
      ),
    },

    {
      title: "Action",
      width: "20%",
      render: (row: any) => (
        <Layout>
          <Cell span={6}>
            <Box gap="20px">
              {row.unfulfilled_items_count > 0 && !row.wix_order_id && (
                <Tooltip
                  placement="top"
                  content={`This order could not be fulfilled in WIX because ${row.unfulfilled_items_count} items from this order are not fulfilled yet. Please visit your Faire store and check the fulfillment status and try again.`}
                >
                  <Icons.Shipping color="#F69891" />
                </Tooltip>
              )}
              {row.faire_only_items_count > 0 && !row.wix_order_id && (
                <Tooltip
                  placement="top"
                  content={`This order could not be sent to WIX because ${row.faire_only_items_count} items from this order are not available on wix. Please set matching SKU on both platforms and try again.`}
                >
                  <Icons.DataDisconnect color="#F69891" />
                </Tooltip>
              )}
              {row.number_of_items_with_no_stock_on_wix > 0 && !row.wix_order_id && (
                <Tooltip
                  placement="top"
                  content={`This order could not be sent to WIX because ${row.number_of_items_with_no_stock_on_wix} items have low or no stock available on Wix. Make sure that Wix has sufficient stock for this order.`}
                >
                  <Icons.CancelOrder color="#F69891" />
                </Tooltip>
              )}
              {row.faire_only_items_count === 0 &&
                row.number_of_items_with_no_stock_on_wix === 0 &&
                !row.wix_order_id && (
                  <Button
                    size="tiny"
                    priority="secondary"
                    onClick={() => handleSendToWix(row.order_id)}
                    prefixIcon={
                      isSendingToWix && selectedOrderIdToBeSentToWix === row.order_id ? (
                        <Box marginRight="5px">
                          <Loader size="tiny" />
                        </Box>
                      ) : (
                        <Icons.Upload />
                      )
                    }
                  >
                    {isSendingToWix ? "Sending to wix" : "Send to wix"}
                  </Button>
                )}
              {row.wix_order_id && (
                <Box placeItems="center">
                  <Icons.Check color="#25A55A" />
                  <Text size="tiny" color="gray">
                    Sent to Wix
                  </Text>
                </Box>
              )}
            </Box>
          </Cell>

          <Cell span={6}>
            <IconButton skin="standard" priority="secondary" size="tiny" onClick={() => (row.expanded = !row.expanded)}>
              {row.expanded ? <Icons.ChevronUp /> : <Icons.ChevronDown />}
            </IconButton>
          </Cell>
        </Layout>
      ),
    },
  ];

  /*
  |--------------------------------------------------------------------------
  | Render Order Details
  |--------------------------------------------------------------------------
  |
  */

  const renderOrderDetails = (row: any) => {
    return (
      <Card showShadow hideOverflow>
        <Card.Content>
          <div
            style={{
              margin: "10px",
              display: "flex",
              gap: "12px",
              alignItems: "center",
            }}
          >
            <Text weight="bold">#{row.display_id}</Text>
            {renderBadge(row)}
          </div>
          <Layout>
            <Cell span={8}>
              <Cell span={12}>
                <Card>
                  {row.order_items.map((item: any, index: any) => {
                    return (
                      <TableListItem
                        key={index}
                        showDivider={true}
                        options={[
                          {
                            value: (
                              <Text size="small">
                                <Tooltip content={item.product_name} className="tooltip_div">
                                  <a
                                    className=""
                                    target="_blank"
                                    style={{ color: "black", textDecoration: "none" }}
                                    href={`https://www.faire.com/brand-portal/my-shop/products/${item.product_id}`}
                                  >
                                    {truncateText(item.product_name, 30)}
                                  </a>
                                </Tooltip>
                                <Text size="small" skin="primary">
                                  SKU : {item.sku ? item.sku : <>&mdash;</>}
                                </Text>
                              </Text>
                            ),
                            width: "50%",
                          },
                          {
                            value: (
                              <Text size="small">
                                <span style={{ marginRight: "5px" }}>{item.currency}</span>
                                {item.amount_minor / 100} * {item.quantity} Qty
                              </Text>
                            ),
                            width: "25%",
                          },
                          {
                            value: (
                              <Text size="small" textAlign="center">
                                <span style={{ marginRight: "5px" }}>{item.currency}</span>
                                {((item.amount_minor * item.quantity) / 100).toFixed(2)}
                              </Text>
                            ),
                            width: "25%",
                          },
                        ]}
                      />
                    );
                  })}
                </Card>
              </Cell>

              <Cell span={4}>
                <TableListItem
                  verticalPadding="tiny"
                  options={[
                    {
                      value: (
                        <Text>
                          Sub Total: {row.currency} {(row.sub_total / 100).toFixed(2)}{" "}
                        </Text>
                      ),
                      align: "right",
                    },
                  ]}
                />
                {row.payout_costs.damaged_and_missing_items.amount_minor ? (
                  <TableListItem
                    verticalPadding="tiny"
                    options={[
                      {
                        value: (
                          <Text>
                            <span style={{ marginRight: "5px" }}>(Damages & Missing Fee):</span>
                            {row.payout_costs.damaged_and_missing_items.currency}{" "}
                            {row.payout_costs.damaged_and_missing_items.amount_minor > 0
                              ? `-${(row.payout_costs.damaged_and_missing_items.amount_minor / 100).toFixed(2)}`
                              : "0.00"}
                          </Text>
                        ),
                        align: "right",
                      },
                    ]}
                  />
                ) : (
                  ""
                )}
                <TableListItem
                  verticalPadding="tiny"
                  options={[
                    {
                      value: (
                        <Text>
                          <span style={{ marginRight: "5px" }}>(Commission Fee):</span>
                          {row.payout_costs.commission.currency}{" "}
                          {row.payout_costs.commission.amount_minor > 0
                            ? `${(row.payout_costs.commission.amount_minor / 100).toFixed(2)}`
                            : "0.00"}
                        </Text>
                      ),
                      align: "right",
                    },
                  ]}
                />
                <TableListItem
                  verticalPadding="tiny"
                  options={[
                    {
                      value: (
                        <Text>
                          <span style={{ marginRight: "5px" }}>(Payout Fee):</span>
                          {row.payout_costs.payout_fee.currency}{" "}
                          {row.payout_costs.payout_fee.amount_minor > 0
                            ? `${(row.payout_costs.payout_fee.amount_minor / 100).toFixed(2)}`
                            : "0.00"}
                        </Text>
                      ),
                      align: "right",
                    },
                  ]}
                />
                {row.shipments.length > 0 && (
                  <TableListItem
                    verticalPadding="tiny"
                    options={[
                      {
                        value: (
                          <Text>
                            Shipping Cost: {row?.currency}&nbsp;
                            {(
                              row?.shipments
                                ?.map((shipment: any) => shipment?.maker_cost_cents)
                                ?.reduce((prevVal: any, currentVal: any) => prevVal + currentVal, 0) / 100
                            ).toFixed(2)}
                          </Text>
                        ),
                        align: "right",
                      },
                    ]}
                  />
                )}
                <TableListItem
                  verticalPadding="tiny"
                  options={[
                    {
                      value: (
                        <Text>
                          (Discount): {row.currency} {row.discount_amount > 0 ? `-${row.discount_amount}` : "0.00"}{" "}
                        </Text>
                      ),
                      align: "right",
                    },
                  ]}
                />

                <TableListItem
                  verticalPadding="tiny"
                  options={[
                    {
                      value: (
                        <Text weight="bold">
                          Total Payout: {row.payout_costs.total_payout.currency}{" "}
                          {row.payout_costs.total_payout.amount_minor > 0
                            ? `${(row.payout_costs.total_payout.amount_minor / 100).toFixed(2)}`
                            : "0.00"}
                        </Text>
                      ),
                      align: "right",
                    },
                  ]}
                />
              </Cell>
            </Cell>
            <Cell span={4}>
              <Card>
                <Text weight="bold">Customer Detail:</Text>
                <div>
                  <p className="center_items">
                    <Icons.User color="" />
                    {row.customer.first_name} {row.customer.last_name}
                  </p>
                  <p className="center_items">
                    <Icons.Send />
                    <Text size="small">
                      {row.address.address1}
                      <p>
                        {row.address.city}, {row.address.postal_code},{row.address.country}
                      </p>
                    </Text>
                  </p>
                  <p className="center_items">
                    <Icons.Phone />
                    {row.address.phone_number}
                  </p>
                </div>
              </Card>
            </Cell>
          </Layout>
        </Card.Content>
      </Card>
    );
  };

  /*
  |--------------------------------------------------------------------------
  | Render row details
  |--------------------------------------------------------------------------
  |
  */

  const renderRowDetails = (row: any) => {
    if (row.expandable) {
      return renderOrderDetails(row);
    }
    return null;
  };

  /*
  |--------------------------------------------------------------------------
  | Render Empty State
  |--------------------------------------------------------------------------
  |
  */
  const renderEmptyState = () => (
    <Table.EmptyState
      title={keywords.length ? "No results found" : "No orders found"}
      subtitle={
        keywords.length
          ? "No items matched your search. Try searching for something else."
          : "Couldn't find any orders."
      }
      image={<Image width="120px" height="120px" transparent={true} src={searchLogo} />}
    >
      {keywords.length ? (
        <Button size="small" priority="secondary" onClick={() => setKeywords("")} prefixIcon={<Icons.Dismiss />}>
          Clear Search
        </Button>
      ) : (
        <></>
      )}
    </Table.EmptyState>
  );

  const debounced = useDebouncedCallback(function (value) {
    setKeywords(value);
    setCurrentPageNumber(1);
  }, 1000);

  const fetchSiteData = () => {
    Site.details()
      .then((response) => {
        setSiteDetails(response);
      })
      .catch((err) => {});
  };

  useEffect(() => {
    fetchData();
    fetchSiteData();
  }, []);

  useEffect(() => {
    fetchData();
  }, [keywords, status, date, currentPageNumber]);

  // Check if any order has sync status true
  const hasSyncStatusTrue = orders && orders.some((order: any) => order.sync_status === 1);

  // Review modal display condition
  const displayReviewModalCondition = hasSyncStatusTrue && siteDetails.review_collected === 0;

  return (
    <WixDesignSystemProvider>
      <>
        <Page height="90vh">
          <Page.Header
            title={
              <Box gap={"10px"} align="space-between" verticalAlign="middle">
                Orders <SyncedDate source="order" />
              </Box>
            }
            subtitle={
              <Text size="tiny">
                Here is a list of orders received in your Faire store. If you see <b>‘error icons’</b> in the Action
                column, please hover above the icon to learn why Faire orders are not synced to Wix. <b>Send To Wix </b>
                button is visible after all the errors have been resolved.
              </Text>
            }
            actionsBar={
              <Box direction="vertical" align="center" marginLeft={"20px"}>
                <Tooltip appendTo="window" content="Fetch all your Faire store orders into the app.">
                  <Button
                    onClick={handleImportFaireOrders}
                    prefixIcon={
                      importInProgress ? (
                        <span className="import_progress_loader_margin">
                          <Loader size="tiny" />
                        </span>
                      ) : (
                        <Icons.Download />
                      )
                    }
                    disabled={importInProgress}
                  >
                    {importInProgress ? (
                      <Text skin="standard" weight="bold">
                        Fetching orders ... {progress}%
                      </Text>
                    ) : !importInProgress && isFetchingOrders ? (
                      "Fetching orders ..."
                    ) : (
                      "Fetch Orders"
                    )}
                  </Button>
                </Tooltip>
              </Box>
            }
          />
          <Page.Content>
            <TrialExpiryAlert />
            <Modal isOpen={shouldShowSendToWixConfirmation} onRequestClose={handleCancelSendToWix}>
              <MessageModalLayout
                onCloseButtonClick={handleCancelSendToWix}
                primaryButtonText="Yes"
                primaryButtonOnClick={handleSendToWixConfirmation}
                secondaryButtonText="Cancel"
                secondaryButtonOnClick={handleCancelSendToWix}
                title="Send to Wix"
                content={<Text>Are you sure you want send this order to Wix?</Text>}
              />
            </Modal>
            <Modal isOpen={showConfirmationForImport} onRequestClose={handleConfirmationForImport}>
              <MessageModalLayout
                onCloseButtonClick={() => handleConfirmationForImport(false)}
                primaryButtonText="Yes"
                primaryButtonOnClick={() => handleConfirmationForImport(true)}
                secondaryButtonText="Cancel"
                secondaryButtonOnClick={() => handleConfirmationForImport(false)}
                title="Fetch Orders"
                content={<Text>Are you sure you want to fetch Orders?</Text>}
              />
            </Modal>

            {/* Card Modal  */}
            {displayReviewModalCondition && (
              <CardModal
                setModalShow={setModalShow}
                modalShown={modalShown}
                title="Give Your Review"
                description="How do you like our app? Please give us the review."
                siteId={siteId}
              />
            )}

            {/* Card Modal End*/}

            <Card className={"faire_product_table"}>
              <Table
                data={orders}
                columns={columns}
                rowDetails={(row) => renderRowDetails(row)}
                isRowHighlight={(rowData) => rowData.highlight}
              >
                <Page.Sticky>
                  <Card>
                    <TableToolbar removeVerticalPadding>
                      <TableToolbar.ItemGroup>
                        <Tabs
                          size="medium"
                          hasDivider={false}
                          items={statusOptions}
                          activeId={status}
                          onClick={(value) => handleTabChange(value)}
                        />
                      </TableToolbar.ItemGroup>
                      <TableToolbar.ItemGroup position="end">
                        <TableToolbar.Item>
                          <Box direction="horizontal" gap={"SP2"}>
                            {date !== "" && (
                              <Button
                                size="small"
                                priority="secondary"
                                prefixIcon={<Icons.Dismiss />}
                                onClick={() => {
                                  setDate("");
                                  setStatus(" ");
                                }}
                              >
                                Clear
                              </Button>
                            )}
                            <Button
                              size="small"
                              priority="secondary"
                              prefixIcon={<Icons.ContentFilterSmall />}
                              onClick={openPanel}
                            >
                              Filter
                            </Button>
                          </Box>
                        </TableToolbar.Item>
                        <TableToolbar.Item>
                          <Search
                            value={keywords}
                            placeholder="Product Name/Order ID"
                            size="small"
                            onChange={(e) => debounced(e.target.value)}
                            onClear={() => setKeywords("")}
                          />
                        </TableToolbar.Item>
                      </TableToolbar.ItemGroup>
                    </TableToolbar>
                    <Table.Titlebar />
                  </Card>
                </Page.Sticky>
                {isLoading ? (
                  <Loading title="Fetching Orders" />
                ) : orders.length ? (
                  <Table.Content titleBarVisible={false} />
                ) : (
                  renderEmptyState()
                )}
              </Table>
            </Card>

            <Layout justifyItems="center">
              <Cell>
                <Box margin="SP5 0 0 0">
                  <Pagination currentPage={currentPageNumber} totalPages={totalPage} onChange={pageChangeHandler} />
                </Box>
              </Cell>
            </Layout>
          </Page.Content>
        </Page>

        {/* Side Panel Modal */}
        <SidePanelModal
          closePanel={closePanel}
          showSidePanel={showSidePanel}
          setShowSidePanel={setShowSidePanel}
          items={[
            accordionItemBuilder({
              title: "All Statuses",
              children: (
                <Box direction="vertical" width="100%">
                  <DatePicker
                    className="datepicker"
                    width={"100%"}
                    size="small"
                    placeholderText="Order Date"
                    showMonthDropdown
                    showYearDropdown
                    onChange={handleDateChange}
                    onClear={() => setDate("")}
                  />
                </Box>
              ),
            }),
          ]}
        />
        {/* Side Panel Modal End*/}
      </>
    </WixDesignSystemProvider>
  );
};

export default withProviders(Index);
